import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    LinearProgress,
    Chip,
    Grid,
    Paper,
    Divider
} from '@mui/material';
import {
    Star as StarIcon,
    MonetizationOn as TfiatIcon,
    TrendingUp as ProgressIcon
} from '@mui/icons-material';
import { userService } from '../../services/api';

interface StarSummary {
    total_calificadas: number;
    active_calificadas: number;
    converted_calificadas: number;
    unrealized_tfiats: number;
    next_conversion: number;
}

const StarSystem: React.FC = () => {
    const [starSummary, setStarSummary] = useState<StarSummary | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadStarSummary();
    }, []);

    const loadStarSummary = async () => {
        try {
            setLoading(true);
            const response = await userService.getStarSummary();
            setStarSummary(response.data);
        } catch (error) {
            console.error('Error al cargar resumen de estrellas:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Card>
                <CardContent>
                    <Typography>Cargando sistema de estrellas...</Typography>
                    <LinearProgress />
                </CardContent>
            </Card>
        );
    }

    if (!starSummary) {
        return (
            <Card>
                <CardContent>
                    <Typography color="error">Error al cargar el sistema de estrellas</Typography>
                </CardContent>
            </Card>
        );
    }

    const progressToNextConversion = ((3 - starSummary.next_conversion) / 3) * 100;

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6" component="h2" gutterBottom sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    color: '#1976d2',
                    fontWeight: 'bold'
                }}>
                    <StarIcon color="primary" />
                    Sistema de Estrellas Calificadas
                </Typography>

                <Grid container spacing={3}>
                    {/* Estrellas Calificadas Activas */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                                <StarIcon sx={{ fontSize: 40, color: '#FFD700', mr: 1 }} />
                                <Typography variant="h4" component="span" color="primary">
                                    {starSummary.active_calificadas}
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                Estrellas Calificadas Activas
                            </Typography>
                            
                            {starSummary.active_calificadas > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Progreso hacia la próxima conversión:
                                    </Typography>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={progressToNextConversion} 
                                        sx={{ height: 8, borderRadius: 4 }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        Faltan {starSummary.next_conversion} estrellas para convertir a Tfiats
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* Tfiats No Realizados */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                                <TfiatIcon sx={{ fontSize: 40, color: '#10B981', mr: 1 }} />
                                <Typography variant="h4" component="span" sx={{ color: '#10B981' }}>
                                    {starSummary.unrealized_tfiats.toFixed(2)}
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                Tfiats en Balance No Realizado
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                Convertidos desde estrellas calificadas
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Estadísticas Detalladas */}
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <Box sx={{ textAlign: 'center', p: 1 }}>
                            <Typography variant="h6" color="primary">
                                {starSummary.total_calificadas}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total de Estrellas Ganadas
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Box sx={{ textAlign: 'center', p: 1 }}>
                            <Typography variant="h6" color="success.main">
                                {starSummary.converted_calificadas}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Estrellas Convertidas
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Box sx={{ textAlign: 'center', p: 1 }}>
                            <Typography variant="h6" color="warning.main">
                                {starSummary.active_calificadas}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Estrellas Pendientes
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

            </CardContent>
        </Card>
    );
};

export default StarSystem;
